import {
  ALL_DISTRIBUTION_CHANNELS_QUERY,
  DISTRIBUTION_CHANNEL_FORMATS_QUERY,
  ENABLED_DISTRIBUTION_CHANNELS_QUERY,
} from '@src/gql/distribution-channel-gql';
import { LogManager } from '@src/utility/Logger';

import { isStaleVersionError } from './isStaleVersionError';

import type { Client } from '@datocms/cma-client-browser';
import type ApiClient from '@src/apiClient/ApiClient';
import type {
  AttachmentOption,
  DistributionChannel,
  DistributionChannelFormat,
  DistributionChannelType,
  DistributionFormatType,
} from '@src/types.ts/distribution-channel';
import type { GraphQLClient } from 'graphql-request';

const readEnabledIds = (record: unknown): string[] => {
  const raw = (record as { enabled_distribution_channels?: unknown })
    .enabled_distribution_channels;
  return Array.isArray(raw) ? (raw as string[]) : [];
};

const MAX_OPTIMISTIC_LOCK_RETRIES = 3;

const logger = LogManager.getLogger('repositories:DistributionChannel');

type RawChannel = {
  id: string;
  title: string;
  internalTitle: string;
  slug: string;
  description: string | null;
  channelType: DistributionChannelType;
  icon: { url: string } | null;
};

type RawChannelWithAvailability = RawChannel & {
  availableToShowcases: { id: string }[];
};

type RawAttachmentBlock = {
  _modelApiKey: string;
  id: string;
  [key: string]: unknown;
};

type RawFormat = {
  id: string;
  title: string;
  internalTitle: string;
  formatType: DistributionFormatType;
  titleMaxChars: number | null;
  textMaxChars: number;
  totalAttachmentsMax: number;
  allowMixingAttachmentTypes: boolean;
  additionalConstraintsJson: object | null;
  attachments: RawAttachmentBlock[];
  channel: { id: string };
};

const splitCsv = (value: string | null | undefined): string[] => {
  if (!value) return [];
  return value
    .split(',')
    .map(s => s.trim())
    .filter(s => s.length > 0);
};

const splitCsvNumbers = (value: string | null | undefined): number[] =>
  splitCsv(value)
    .map(s => Number(s))
    .filter(n => Number.isFinite(n));

const nullIfEmpty = (value: string | null | undefined): string | null =>
  value && value.length > 0 ? value : null;

const mapAttachment = (block: RawAttachmentBlock): AttachmentOption | null => {
  switch (block._modelApiKey) {
    case 'attachment_image_block':
      return {
        type: 'image',
        role: (block.role as string | null) ?? null,
        maxCount: (block.maxCount as number) ?? 0,
        maxSizeMb: (block.maxSizeMb as number) ?? 0,
        minDimensions: nullIfEmpty(block.minDimensions as string | null),
        maxDimensions: nullIfEmpty(block.maxDimensions as string | null),
        aspectRatios: splitCsv(block.aspectRatios as string | null),
        mimeTypes: splitCsv(block.mimeTypes as string | null),
        altTextMaxChars: (block.altTextMaxChars as number | null) ?? null,
        additionalConstraintsJson:
          (block.additionalConstraintsJson as object | null) ?? null,
      };
    case 'attachment_video_block':
      return {
        type: 'video',
        role: (block.role as string | null) ?? null,
        maxCount: (block.maxCount as number) ?? 0,
        minSeconds: (block.minSeconds as number) ?? 0,
        maxSeconds: (block.maxSeconds as number) ?? 0,
        maxSizeMb: (block.maxSizeMb as number) ?? 0,
        minDimensions: nullIfEmpty(block.minDimensions as string | null),
        maxDimensions: nullIfEmpty(block.maxDimensions as string | null),
        aspectRatios: splitCsv(block.aspectRatios as string | null),
        frameRateMin: (block.frameRateMin as number | null) ?? null,
        frameRateMax: (block.frameRateMax as number | null) ?? null,
        codecs: splitCsv(block.codecs as string | null),
        mimeTypes: splitCsv(block.mimeTypes as string | null),
        coverImageSupported: Boolean(block.coverImageSupported),
        captionsSupported: Boolean(block.captionsSupported),
        additionalConstraintsJson:
          (block.additionalConstraintsJson as object | null) ?? null,
      };
    case 'attachment_document_block':
      return {
        type: 'document',
        maxCount: (block.maxCount as number) ?? 0,
        maxPages: (block.maxPages as number) ?? 0,
        maxSizeMb: (block.maxSizeMb as number) ?? 0,
        mimeTypes: splitCsv(block.mimeTypes as string | null),
        additionalConstraintsJson:
          (block.additionalConstraintsJson as object | null) ?? null,
      };
    case 'attachment_link_share_block':
      return {
        type: 'link_share',
        maxCount: (block.maxCount as number) ?? 0,
        additionalConstraintsJson:
          (block.additionalConstraintsJson as object | null) ?? null,
      };
    case 'attachment_poll_block':
      return {
        type: 'poll',
        maxCount: (block.maxCount as number) ?? 0,
        questionMaxChars: (block.questionMaxChars as number) ?? 0,
        optionsMin: (block.optionsMin as number) ?? 0,
        optionsMax: (block.optionsMax as number) ?? 0,
        optionMaxChars: (block.optionMaxChars as number) ?? 0,
        durationOptionsDays: splitCsvNumbers(
          block.durationOptionsDays as string | null,
        ),
        additionalConstraintsJson:
          (block.additionalConstraintsJson as object | null) ?? null,
      };
    default:
      logger.warn('Unknown attachment block type — skipping', {
        modelApiKey: block._modelApiKey,
        blockId: block.id,
      });
      return null;
  }
};

const mapFormat = (raw: RawFormat): DistributionChannelFormat => ({
  id: raw.id,
  title: raw.title,
  internalTitle: raw.internalTitle,
  formatType: raw.formatType,
  titleMaxChars: raw.titleMaxChars,
  textMaxChars: raw.textMaxChars,
  totalAttachmentsMax: raw.totalAttachmentsMax,
  allowMixingAttachmentTypes: raw.allowMixingAttachmentTypes,
  additionalConstraintsJson: raw.additionalConstraintsJson,
  attachments: raw.attachments
    .map(mapAttachment)
    .filter((a): a is AttachmentOption => a !== null),
});

const mapChannel = (
  raw: RawChannel,
  formats: DistributionChannelFormat[],
): DistributionChannel => ({
  id: raw.id,
  title: raw.title,
  internalTitle: raw.internalTitle,
  slug: raw.slug,
  description: raw.description,
  iconUrl: raw.icon?.url ?? null,
  channelType: raw.channelType,
  formats,
});

export class DistributionChannelRepository {
  private gqlClient: GraphQLClient;
  private dClient: Client | ApiClient;

  constructor(gqlClient: GraphQLClient, dClient: Client | ApiClient) {
    this.gqlClient = gqlClient;
    this.dClient = dClient;
  }

  async findAvailableForShowcase(
    showcaseId: string,
  ): Promise<DistributionChannel[]> {
    const resp = await this.gqlClient.request<{
      allDistributionChannels: RawChannelWithAvailability[];
    }>(ALL_DISTRIBUTION_CHANNELS_QUERY);

    const available = resp.allDistributionChannels.filter(c => {
      const list = c.availableToShowcases;
      if (!list || list.length === 0) return true;
      return list.some(s => s.id === showcaseId);
    });

    if (available.length === 0) return [];

    const formatResp = await this.gqlClient.request<{
      allDistributionChannelFormats: RawFormat[];
    }>(DISTRIBUTION_CHANNEL_FORMATS_QUERY, {
      channelIds: available.map(c => c.id),
    });

    const formatsByChannel = new Map<string, DistributionChannelFormat[]>();
    for (const rawFormat of formatResp.allDistributionChannelFormats) {
      const channelId = rawFormat.channel.id;
      const existing = formatsByChannel.get(channelId) ?? [];
      existing.push(mapFormat(rawFormat));
      formatsByChannel.set(channelId, existing);
    }

    return available.map(raw =>
      mapChannel(raw, sortFormats(formatsByChannel.get(raw.id) ?? [])),
    );
  }

  async enableChannelForShowcase(
    showcaseId: string,
    channelId: string,
  ): Promise<void> {
    await this.mutateEnabledChannels(
      showcaseId,
      channelId,
      'enable',
      current => (current.includes(channelId) ? null : [...current, channelId]),
    );
  }

  async disableChannelForShowcase(
    showcaseId: string,
    channelId: string,
  ): Promise<void> {
    await this.mutateEnabledChannels(
      showcaseId,
      channelId,
      'disable',
      current =>
        current.includes(channelId)
          ? current.filter(id => id !== channelId)
          : null,
    );
  }

  private async mutateEnabledChannels(
    showcaseId: string,
    channelId: string,
    op: 'enable' | 'disable',
    computeNext: (current: string[]) => string[] | null,
  ): Promise<void> {
    for (let attempt = 0; attempt <= MAX_OPTIMISTIC_LOCK_RETRIES; attempt++) {
      const record = await this.dClient.items.find(showcaseId);
      const next = computeNext(readEnabledIds(record));
      if (next === null) return;

      try {
        await this.dClient.items.update(showcaseId, {
          enabled_distribution_channels: next,
          meta: { current_version: record.meta.current_version },
        });
        await this.dClient.items.publish(showcaseId);
        return;
      } catch (error) {
        if (
          isStaleVersionError(error) &&
          attempt < MAX_OPTIMISTIC_LOCK_RETRIES
        ) {
          logger.warn('Stale version, retrying', {
            op,
            showcaseId,
            channelId,
            attempt,
          });
          continue;
        }
        throw error;
      }
    }
    throw new Error(
      `Exhausted optimistic-lock retries (${op} ${channelId} on ${showcaseId})`,
    );
  }

  async findEnabledForShowcase(
    showcaseId: string,
  ): Promise<DistributionChannel[]> {
    const channelResp = await this.gqlClient.request<{
      showcase: { enabledDistributionChannels: RawChannel[] } | null;
    }>(ENABLED_DISTRIBUTION_CHANNELS_QUERY, { id: showcaseId });

    if (channelResp.showcase == null) {
      logger.warn(
        'Showcase not found when loading enabled distribution channels',
        { showcaseId },
      );
      return [];
    }
    const rawChannels = channelResp.showcase.enabledDistributionChannels;
    if (rawChannels.length === 0) return [];

    const formatResp = await this.gqlClient.request<{
      allDistributionChannelFormats: RawFormat[];
    }>(DISTRIBUTION_CHANNEL_FORMATS_QUERY, {
      channelIds: rawChannels.map(c => c.id),
    });

    const formatsByChannel = new Map<string, DistributionChannelFormat[]>();
    for (const rawFormat of formatResp.allDistributionChannelFormats) {
      const channelId = rawFormat.channel.id;
      const existing = formatsByChannel.get(channelId) ?? [];
      existing.push(mapFormat(rawFormat));
      formatsByChannel.set(channelId, existing);
    }

    return rawChannels.map(raw =>
      mapChannel(raw, sortFormats(formatsByChannel.get(raw.id) ?? [])),
    );
  }
}

const FORMAT_TYPE_ORDER: DistributionFormatType[] = [
  'feed_post',
  'short_form_video',
  'long_form_video',
  'photo_carousel',
  'story',
  'article',
];

const sortFormats = (
  formats: DistributionChannelFormat[],
): DistributionChannelFormat[] =>
  [...formats].sort(
    (a, b) =>
      FORMAT_TYPE_ORDER.indexOf(a.formatType) -
      FORMAT_TYPE_ORDER.indexOf(b.formatType),
  );
